import { Dispatch, SetStateAction, useEffect, useState } from "react"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { CustomInputGroups, CustomInputTypeEnum } from "types"
import { filterInputName } from "utils/filter"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import ToggleInput from "components/inputs/toggleInput"
import SelectInput from "components/inputs/selectInput"
import useLayout from "hooks/useLayout"
import { ReactComponent as SaveIcon } from "assets/icons/save.svg"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import Form from "components/forms/form"

interface EditCustomInputFormProps {
    customInputKey: string,
    groups: CustomInputGroups,
    setGroups: Dispatch<SetStateAction<CustomInputGroups>>
    groupName: string
}

const EditCustomInputForm: React.FC<EditCustomInputFormProps> = ({ customInputKey, groups, setGroups, groupName }) => {

    let customInputsInGroup = { ...groups[groupName].customInputs }
    const customInput = { ...customInputsInGroup[customInputKey] }

    const { t } = useTranslation()
    const [errors, setErrors] = useState<string[]>([]);

    const [name, setName] = useState<string>(() => customInputKey)
    const [type, setType] = useState<CustomInputTypeEnum>(() => customInput.type)
    const [required, setRequired] = useState<boolean>(() => customInput.required)
    const [defaultValue, setDefaultValue] = useState<string | number | boolean>(() => customInput.defaultValue)
    const [options, setOptions] = useState<string[]>(() => ('options' in customInput ? customInput.options ?? [] : []))
    const [optionsText, setOptionsText] = useState<string>(() => ('options' in customInput && customInput.options ? customInput.options.join('\n') : ``))

    const {
        closeDialog
    } = useLayout()

    // Removed effect that resets state on every prop change to prevent resetting user input.

    // Handle form submission.
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        // Check if the name is already in use.
        if (name !== customInputKey && Object.keys(customInputsInGroup).includes(name)) {
            setErrors([t('nameAlreadyExists')])
            return
        }
        // Create new group.
        let newGroups = {
            ...groups,
            [groupName]: {
                ...groups[groupName],
                customInputs: { ...customInputsInGroup }
            }
        }
        delete newGroups[groupName].customInputs[customInputKey]
        
        // Insert new custom field.
        newGroups[groupName].customInputs[name] = {
            type,
            required,
            defaultValue,
            ...(type === 'select' || type === 'radio' ? { options: options || [] } : {})
        } as any
        // If the name has changed, update the group key.
        if (name !== customInputKey) {
            const currentOrder = newGroups[groupName].customInputOrder || []
            newGroups = {
                ...newGroups,
                [groupName]: {
                    ...newGroups[groupName],
                    customInputOrder: currentOrder.map((key) => key === customInputKey ? name : key)
                }
            }
        }
        setGroups(newGroups)
        // Close the dialog.
        closeDialog()
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(filterInputName(e.target.value, false, 30, true))
    }

    // Handle type change.
    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const targetType = e.target.value as CustomInputTypeEnum
        setType(targetType)
        if (targetType === CustomInputTypeEnum.CHECKBOX) setDefaultValue(`false`)
        if (targetType === CustomInputTypeEnum.SELECT || targetType === CustomInputTypeEnum.RADIO) {
            if (options.length === 0) {
                setDefaultValue(``)
                setOptions([])
                setOptionsText(``)
            }
        }
    }

    // Handle default is checked change.
    const handleDefaultIsCheckedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDefaultValue(e.target.checked ? `true` : `false`)
    }

    // Handle required change.
    const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRequired(e.target.checked)
    }

    // Handle default value change.
    const handleDefaultValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDefaultValue(e.target.value)
    }

    // Handle options text change.
    const handleOptionsTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value
        setOptionsText(text)
        // Split by newlines and filter out empty strings
        const optionsList = text.split('\n').filter(option => option.trim() !== '')
        setOptions(optionsList)
    }

    // Return the JSX.
    return (
        <Form onSubmit={handleSubmit}>
            <Section>
            <div className={`group`}>

            <TextInput label={t('name')} value={name} onChange={handleNameChange} required />
            
            <SelectInput
                label={t('type')}
                value={type}
                options={[
                    { value: `text`, label: t('text') },
                    { value: `number`, label: t('number') },
                    { value: `checkbox`, label: t('checkbox') },
                    { value: `textarea`, label: t('textarea') },
                    { value: `select`, label: t('select') },
                    { value: `radio`, label: t('radio') }
                ]}
                onChange={(e) => handleTypeChange(e)}
                required />

            <ToggleInput label={t('required')} checked={required} onChange={handleRequiredChange} />

            {type !== `checkbox` && type !== `select` && type !== `radio` && (
                <TextInput 
                    label={t('defaultValue')} 
                    value={String(defaultValue)} 
                    onChange={handleDefaultValueChange}
                    maxLength={type === 'textarea' ? 2000 : 200}
                    showCharacterCount={true}
                />
            )}

            {type === `checkbox` && (
                <ToggleInput label={t('defaultIsChecked')} checked={defaultValue === 'true'} onChange={handleDefaultIsCheckedChange} />
            )}

            {(type === `select` || type === `radio`) && (
                <TextAreaInput 
                    label={t('options')} 
                    value={optionsText} 
                    onChange={handleOptionsTextChange} 
                    placeholder={t('enterOptionsOnePerLine')} 
                    rows={4}
                    required
                />
            )}

            <ButtonGroup>
                <Button wideOnMobile type="button" design="white" onClick={closeDialog}><CloseIcon /><span>{t('buttonCancel')}</span></Button>
                <Button wideOnMobile type="submit" design="primary"><SaveIcon /><span>{t('buttonSave')}</span></Button>
            </ButtonGroup>

            {errors.length > 0 && <FormErrors errors={errors} />}
        </div>
            </Section>
        </Form>
    )

}

export default EditCustomInputForm



